<!--
  乘车过场播放器：全屏三层视差街景 + 一辆疾驰的大巴，逐站报站。

  为什么单独一个组件：coachPanel 管「选班次 / 扣费 / 落地场景」，过场只是其中一段表现，
  混在一起后 coachPanel 里的定时器与弹窗生命周期会互相干扰（过场中途关窗就是典型）。

  ⚠ 这里刻意没有任何跳过入口（不绑 click、不绑 ESC）：过场期间后端已经完成位移与扣费，
  「中途关窗即快进」的旧逻辑只在 coachPanel 的 before-close 里保留一处，避免出现
  「人已到新站、界面还停在旧街」的半落地状态。

  场景切换用「每层两套条纹叠着、靠 opacity 交叉淡化」而不是「淡到透明再换图」：
  后者等于每站都黑屏一次，1.5s 一站时非常扎眼。两套条纹同时挂载还有个好处 ——
  CSS 动画起始时间一致，交叉淡化时滚动速度不会跳。
-->
<template>
  <div class="cp-stage">
    <!-- 三层视差：层高度必须与 utils/coachScene.js 顶部的分层约定一致 -->
    <div class="cp-layer cp-layer--far">
      <div class="cp-strip cp-strip--far" :class="{ show: showA }">
        <i :style="{ backgroundImage: a.far }"></i><i :style="{ backgroundImage: a.far }"></i>
      </div>
      <div class="cp-strip cp-strip--far" :class="{ show: !showA }">
        <i :style="{ backgroundImage: b.far }"></i><i :style="{ backgroundImage: b.far }"></i>
      </div>
    </div>
    <div class="cp-layer cp-layer--mid">
      <div class="cp-strip cp-strip--mid" :class="{ show: showA }">
        <i :style="{ backgroundImage: a.mid }"></i><i :style="{ backgroundImage: a.mid }"></i>
      </div>
      <div class="cp-strip cp-strip--mid" :class="{ show: !showA }">
        <i :style="{ backgroundImage: b.mid }"></i><i :style="{ backgroundImage: b.mid }"></i>
      </div>
    </div>
    <div class="cp-layer cp-layer--near">
      <div class="cp-strip cp-strip--near" :class="{ show: showA }">
        <i :style="{ backgroundImage: a.near }"></i><i :style="{ backgroundImage: a.near }"></i>
      </div>
      <div class="cp-strip cp-strip--near" :class="{ show: !showA }">
        <i :style="{ backgroundImage: b.near }"></i><i :style="{ backgroundImage: b.near }"></i>
      </div>
    </div>

    <!-- 大巴 -->
    <div class="cp-bus">
      <div class="cp-bus-bob" v-html="busSvg"></div>
    </div>

    <div class="cp-vignette"></div>

    <!-- 报站 HUD -->
    <div class="cp-hud">
      <div class="cp-hud-top">
        <div class="cp-route">{{ routeLine }}</div>
        <div class="cp-tag">{{ tag }}</div>
      </div>

      <div class="cp-hud-center">
        <div class="cp-kicker">正在经过</div>
        <div :key="idx" class="cp-station">{{ curName }}</div>
      </div>

      <div class="cp-hud-bottom">
        <div v-if="curDesc" class="cp-desc">{{ curDesc }}</div>
        <div class="cp-bar"><i :style="{ width: percent + '%' }"></i></div>
        <div class="cp-meta">
          <span>第 {{ idx + 1 }} / {{ total }} 站</span>
          <span class="cp-next">{{ nextText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getScene, matchSceneKey, buildBusSvg } from '../../utils/coachScene'

const EMPTY = { far: '', mid: '', near: '' }

export default {
  name: 'coachPassage',
  props: {
    // [{ id, name, desc }] —— 后端 /map/coach/ride 返回的沿途站序
    stations: {
      type: Array,
      default: () => []
    },
    routeLine: {
      type: String,
      default: ''
    },
    // 每站停留时长：24 站的维幻线约 36s。嫌长就调小这一处。
    stepMs: {
      type: Number,
      default: 1500
    }
  },
  data() {
    return {
      idx: 0,
      // a / b 两套背景轮流当「前台」，靠 CSS opacity 交叉淡化
      a: Object.assign({}, EMPTY),
      b: Object.assign({}, EMPTY),
      showA: true,
      curKey: '',
      tag: '',
      busSvg: '',
      timerId: null,
      arrived: false
    }
  },
  computed: {
    total() {
      return this.stations.length
    },
    curStation() {
      return this.stations[this.idx] || {}
    },
    curName() {
      return this.curStation.name || ''
    },
    curDesc() {
      const d = this.curStation.desc || ''
      // 描述可能很长（有的节点几百字），报站条只放得下一行
      return d.length > 46 ? d.slice(0, 46) + '…' : d
    },
    percent() {
      if (this.total <= 1) return 100
      return Math.round(((this.idx + 1) / this.total) * 1000) / 10
    },
    nextText() {
      const n = this.stations[this.idx + 1]
      return n ? '下一站 · ' + n.name : '即将到站'
    }
  },
  mounted() {
    // 必须自己套 <svg> 外壳：v-html 的目标是 div，裸的 <rect>/<path> 在 HTML 解析下
    // 不属于 SVG 命名空间，一条都画不出来（效果图里是塞进 <svg> 容器才正常的）。
    this.busSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 180">'
      + buildBusSvg() + '</svg>'
    this.paint(true)
    this.timerId = setInterval(this.tick, this.stepMs)
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  },
  methods: {
    tick() {
      // 最后一站再停满一拍才落地：到站的一瞬间就切场景的话，玩家看不到到达站长什么样
      if (this.idx >= this.total - 1) {
        this.finish()
        return
      }
      this.idx += 1
      this.paint(false)
    },
    paint(first) {
      const st = this.curStation
      const key = matchSceneKey(st.name, st.desc)
      // 同类相邻站（维幻线连着 5 个停车区 / 5 个出口）背景一模一样，
      // 还去做淡化就只是白闪一下，直接跳过。
      if (!first && key === this.curKey) {
        return
      }
      this.curKey = key
      const scene = getScene(key)
      // 首帧写进 a（此时 showA=true 正好可见）；之后每次写进当前不可见的那一套，再翻牌
      const target = first ? this.a : (this.showA ? this.b : this.a)
      this.$set(target, 'far', scene.far)
      this.$set(target, 'mid', scene.mid)
      this.$set(target, 'near', scene.near)
      this.tag = scene.tag
      if (!first) {
        this.showA = !this.showA
      }
    },
    finish() {
      if (this.arrived) return
      this.arrived = true
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
      this.$emit('finish')
    }
  }
}
</script>

<style scoped>
.cp-stage {
  position: fixed; left: 0; top: 0; right: 0; bottom: 0; z-index: 9999;
  overflow: hidden; background: #11161c; isolation: isolate;
  animation: cp-in .3s ease-out;
}
@keyframes cp-in { from { opacity: 0; } to { opacity: 1; } }

/* ---------- 视差层 ---------- */
.cp-layer { position: absolute; left: 0; right: 0; overflow: hidden; }
.cp-layer--far { top: 0; height: 74%; z-index: 1; }
.cp-layer--mid { top: 34%; height: 34%; z-index: 2; }
.cp-layer--near { top: 70%; height: 30%; z-index: 3; }

.cp-strip {
  position: absolute; top: 0; left: 0; width: 200%; height: 100%;
  display: flex; will-change: transform, opacity;
  opacity: 0; transition: opacity .42s ease;
}
.cp-strip.show { opacity: 1; }
.cp-strip i { display: block; width: 50%; height: 100%; background-repeat: repeat-x; background-size: 100% 100%; }
@keyframes cp-slide { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.cp-strip--far { animation: cp-slide 150s linear infinite; }
.cp-strip--mid { animation: cp-slide 22s linear infinite; }
.cp-strip--near { animation: cp-slide 5.5s linear infinite; }

/* ---------- 大巴 ---------- */
.cp-bus {
  position: absolute; left: 17%; bottom: 4.5%; width: 41%; z-index: 8;
  filter: drop-shadow(0 18px 18px rgba(0, 0, 0, .42));
}
.cp-bus >>> svg { display: block; width: 100%; height: auto; }
.cp-bus-bob { animation: cp-bob .42s ease-in-out infinite alternate; transform-origin: 50% 88%; }
@keyframes cp-bob {
  from { transform: translateY(0) rotate(-.25deg); }
  to { transform: translateY(-3.5px) rotate(.25deg); }
}
.cp-bus >>> .cp-wheel { animation: cp-spin .42s linear infinite; }
@keyframes cp-spin { to { transform: rotate(360deg); } }
.cp-bus >>> .cp-speed { animation: cp-dash .6s linear infinite; }
@keyframes cp-dash {
  from { opacity: 0; transform: translateX(14px); }
  40% { opacity: .85; }
  to { opacity: 0; transform: translateX(-46px); }
}
.cp-bus >>> .cp-beam { animation: cp-beam 2.6s ease-in-out infinite; }
@keyframes cp-beam { 0%, 100% { opacity: .34; } 50% { opacity: .62; } }

/* ---------- HUD ---------- */
.cp-vignette {
  position: absolute; inset: 0; z-index: 9; pointer-events: none;
  background: radial-gradient(ellipse at 50% 52%, rgba(0, 0, 0, 0) 42%, rgba(0, 0, 0, .42) 100%);
}
.cp-hud { position: absolute; inset: 0; z-index: 10; pointer-events: none; }
.cp-hud-top {
  position: absolute; top: 0; left: 0; right: 0; padding: 22px 30px;
  background: linear-gradient(180deg, rgba(6, 9, 13, .72), rgba(6, 9, 13, 0));
  display: flex; justify-content: space-between; align-items: flex-start;
}
.cp-route { font-size: 13px; letter-spacing: 3px; color: #e6cf9a; opacity: .92; }
.cp-tag {
  font-size: 11px; letter-spacing: 2px; color: #b9c6d3;
  border: 1px solid rgba(185, 198, 211, .32); padding: 3px 9px; border-radius: 20px;
  background: rgba(10, 14, 19, .35);
}
.cp-hud-center { position: absolute; left: 0; right: 0; top: 20%; text-align: center; }
.cp-kicker { font-size: 12px; letter-spacing: 6px; color: #cfd9e3; opacity: .7; margin-bottom: 10px; }
.cp-station {
  font-size: 42px; font-weight: 600; letter-spacing: 6px; color: #fdf3dd;
  text-shadow: 0 2px 20px rgba(0, 0, 0, .55), 0 0 30px rgba(230, 207, 154, .35);
  animation: cp-st-in .5s cubic-bezier(.2, .9, .3, 1);
}
@keyframes cp-st-in {
  from { opacity: 0; transform: translateY(9px) scale(.975); filter: blur(3px); }
  to { opacity: 1; transform: none; filter: none; }
}
.cp-hud-bottom {
  position: absolute; left: 0; right: 0; bottom: 0; padding: 26px 30px 22px;
  background: linear-gradient(0deg, rgba(6, 9, 13, .8), rgba(6, 9, 13, 0));
}
.cp-desc {
  font-size: 11.5px; letter-spacing: .6px; color: rgba(232, 240, 246, .5);
  margin-bottom: 13px; max-width: 62%;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.cp-bar { height: 2px; background: rgba(255, 255, 255, .16); border-radius: 2px; overflow: hidden; }
.cp-bar i {
  display: block; height: 100%; width: 0;
  background: linear-gradient(90deg, #a9761c, #f0d9a8);
  box-shadow: 0 0 10px rgba(240, 217, 168, .6);
  transition: width .55s cubic-bezier(.3, .8, .3, 1);
}
.cp-meta {
  display: flex; justify-content: space-between; margin-top: 9px;
  font-size: 11.5px; letter-spacing: 1.4px; color: #9fadba;
}
.cp-meta .cp-next { color: #cbb98a; }
</style>
